import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  CheckCircle2, XCircle, Clock, Eye, Filter, RefreshCw, ClipboardList,
  Calendar, Building2,
} from 'lucide-react';
import { usePlanningGateQueue, usePlanningGateMutation, PlanningGateItem } from '@/hooks/usePlanningGate';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

const formatDate = (d: string | null) => {
  if (!d) return '—';
  try { return format(parseISO(d.includes('T') ? d : d + 'T00:00:00'), 'dd/MM/yyyy', { locale: ptBR }); }
  catch { return d; }
};

export default function PlanningGatePage() {
  const navigate = useNavigate();
  const { data: queue = [], isLoading, refetch, isFetching } = usePlanningGateQueue();
  const mutation = usePlanningGateMutation();

  const [contratoFilter, setContratoFilter] = useState('all');
  const [search, setSearch] = useState('');

  // Dialog state
  const [dialogItem, setDialogItem] = useState<PlanningGateItem | null>(null);
  const [decision, setDecision] = useState<'aprovada' | 'rejeitada'>('aprovada');
  const [obs, setObs] = useState('');

  const contratos = useMemo(() => {
    const map = new Map<string, string>();
    queue.forEach(i => map.set(i.contrato_id, `${i.contrato_numero} — ${i.contrato_nome}`));
    return Array.from(map.entries()).map(([id, label]) => ({ id, label }));
  }, [queue]);

  const filtered = useMemo(() => {
    return queue.filter(item => {
      if (contratoFilter !== 'all' && item.contrato_id !== contratoFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const matches =
          item.item_codigo?.toLowerCase().includes(q) ||
          item.item_descricao?.toLowerCase().includes(q) ||
          item.contrato_nome?.toLowerCase().includes(q) ||
          item.contrato_numero?.toLowerCase().includes(q) ||
          item.localizacao_descricao?.toLowerCase().includes(q);
        if (!matches) return false;
      }
      return true;
    });
  }, [queue, contratoFilter, search]);

  const openDialog = (item: PlanningGateItem, dec: 'aprovada' | 'rejeitada') => {
    setDialogItem(item);
    setDecision(dec);
    setObs('');
  };

  const handleConfirm = () => {
    if (!dialogItem) return;
    mutation.mutate({
      execucaoId: dialogItem.id,
      contratoId: dialogItem.contrato_id,
      decision,
      observacoes: obs,
    }, {
      onSuccess: () => {
        setDialogItem(null);
        setObs('');
      },
    });
  };

  const canConfirm = decision === 'aprovada' ? true : obs.trim().length >= 10;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Portal da Qualidade — Planejamento
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Fila de execuções aguardando aprovação do planejamento
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`h-4 w-4 mr-1.5 ${isFetching ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="border-blue-200 dark:border-blue-800">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <Clock className="h-3.5 w-3.5 text-blue-500" /> Aguardando Planejamento
            </div>
            <p className="text-2xl font-bold text-blue-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {queue.length}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">em fila</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <Building2 className="h-3.5 w-3.5 text-primary" /> Contratos Envolvidos
            </div>
            <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {contratos.length}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">com pendências</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" /> Filtrados
            </div>
            <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {filtered.length}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">resultado(s)</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-3">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Filter className="h-4 w-4" /> Filtros
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs mb-1 block">Busca</Label>
              <Input
                placeholder="Item, descrição, contrato..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <div className="min-w-[180px]">
              <Label className="text-xs mb-1 block">Contrato</Label>
              <Select value={contratoFilter} onValueChange={setContratoFilter}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os contratos</SelectItem>
                  {contratos.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Queue Table */}
      <Card>
        <CardHeader className="py-3 flex flex-row items-center gap-2">
          <ClipboardList className="h-4 w-4 text-primary" />
          <CardTitle className="text-sm font-medium">
            Fila de Aprovação do Planejamento
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500/40" />
              <p className="text-base font-medium">Fila limpa</p>
              <p className="text-sm mt-1">
                {queue.length === 0
                  ? 'Nenhuma execução aguardando aprovação do planejamento.'
                  : 'Nenhum resultado para os filtros aplicados.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Contrato</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Qtd</TableHead>
                  <TableHead>Local</TableHead>
                  <TableHead>Qualidade</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(item => (
                  <TableRow key={item.id} className="bg-blue-50/30 dark:bg-blue-950/10">
                    <TableCell className="text-sm font-medium whitespace-nowrap">
                      {formatDate(item.data_execucao)}
                    </TableCell>
                    <TableCell>
                      <div className="text-xs font-medium">{item.contrato_numero}</div>
                      <div className="text-xs text-muted-foreground max-w-[150px] truncate">
                        {item.contrato_nome}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[180px]">
                      <div className="text-xs font-medium">{item.item_codigo || '—'}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {item.item_descricao || '—'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={item.tipo === 'retrabalho' ? 'destructive' : 'outline'}
                        className="text-xs"
                      >
                        {item.tipo === 'retrabalho' ? '↺ Retrab.' : 'Execução'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-sm font-semibold">
                      {item.quantidade_executada} {item.unidade}
                    </TableCell>
                    <TableCell className="max-w-[120px]">
                      <span className="text-xs text-muted-foreground truncate block">
                        {item.localizacao_descricao || '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="default" className="text-xs gap-1 bg-green-600">
                        <CheckCircle2 className="h-3 w-3" /> Aprovada
                      </Badge>
                      {item.qualidade_observacoes && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-xs text-muted-foreground italic cursor-help truncate max-w-[120px] block mt-1">
                              {item.qualidade_observacoes}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            {item.qualidade_observacoes}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => navigate(`/empreendimentos/contratos/${item.contrato_id}/execucao`)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Ver no contrato</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => openDialog(item, 'aprovada')}
                            >
                              <CheckCircle2 className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Aprovar — Disponível para Medição</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => openDialog(item, 'rejeitada')}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Retornar para Qualidade</TooltipContent>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Decision Dialog */}
      <Dialog open={!!dialogItem} onOpenChange={(o) => { if (!o) setDialogItem(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {decision === 'aprovada' ? '✓ Aprovar no Planejamento' : '✗ Retornar para Qualidade'}
            </DialogTitle>
            <DialogDescription>
              {decision === 'aprovada'
                ? 'A execução será liberada para medição.'
                : 'A execução retornará para revisão de qualidade. Justificativa obrigatória (mín. 10 caracteres).'}
            </DialogDescription>
          </DialogHeader>

          {dialogItem && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-muted-foreground">Contrato</span>
                  <p className="font-medium">{dialogItem.contrato_numero}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Item</span>
                  <p className="font-medium">{dialogItem.item_codigo || '—'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Quantidade</span>
                  <p className="font-medium">{dialogItem.quantidade_executada} {dialogItem.unidade}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Data Execução</span>
                  <p className="font-medium">{formatDate(dialogItem.data_execucao)}</p>
                </div>
              </div>

              <div>
                <Label className="text-sm">
                  {decision === 'aprovada' ? 'Observações (opcional)' : 'Justificativa *'}
                </Label>
                <Textarea
                  value={obs}
                  onChange={e => setObs(e.target.value)}
                  placeholder={decision === 'rejeitada'
                    ? 'Descreva o motivo da devolução...'
                    : 'Observações adicionais...'}
                  rows={3}
                  className="mt-1"
                />
                {decision === 'rejeitada' && obs.trim().length > 0 && obs.trim().length < 10 && (
                  <p className="text-xs text-destructive mt-1">Mínimo 10 caracteres</p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogItem(null)}>Cancelar</Button>
            <Button
              onClick={handleConfirm}
              disabled={!canConfirm || mutation.isPending}
              variant={decision === 'aprovada' ? 'default' : 'destructive'}
            >
              {mutation.isPending ? 'Processando...' : decision === 'aprovada' ? 'Aprovar' : 'Retornar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
